import { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useRouter } from 'expo-router';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Plus,
  TrendingUp,
  TrendingDown,
  Calendar,
  ChevronLeft,
  Save,
} from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { FLAGS } from '@/lib/featureFlags';

interface BodyMetric {
  id: string;
  recorded_at: string;
  bodyweight?: number;
  body_fat_percentage?: number;
  biceps?: number;
  chest?: number;
  waist?: number;
  hips?: number;
  thighs?: number;
  calves?: number;
  shoulders?: number;
  neck?: number;
  notes?: string;
}

const MEASUREMENTS = [
  { key: 'bodyweight', label: 'Bodyweight', unit: 'lbs' },
  { key: 'body_fat_percentage', label: 'Body Fat %', unit: '%' },
  { key: 'chest', label: 'Chest', unit: 'in' },
  { key: 'waist', label: 'Waist', unit: 'in' },
  { key: 'hips', label: 'Hips', unit: 'in' },
  { key: 'biceps', label: 'Biceps', unit: 'in' },
  { key: 'thighs', label: 'Thighs', unit: 'in' },
  { key: 'calves', label: 'Calves', unit: 'in' },
  { key: 'shoulders', label: 'Shoulders', unit: 'in' },
  { key: 'neck', label: 'Neck', unit: 'in' },
];

export default function BodyMetricsScreen() {
  const router = useRouter();
  const { profile } = useAuth();
  const { theme } = useTheme();
  const [metrics, setMetrics] = useState<BodyMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Partial<BodyMetric>>({});
  const [saving, setSaving] = useState(false);

  // V1: Body metrics disabled — redirect to home
  useEffect(() => {
    if (!FLAGS.V1_BODY_METRICS_ENABLED) {
      router.replace('/(tabs)/home');
    }
  }, [router]);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    if (!profile) return;

    const { data } = await supabase
      .from('body_metrics')
      .select('*')
      .eq('user_id', profile.id)
      .order('recorded_at', { ascending: false })
      .limit(30);

    if (data) {
      setMetrics(data);
    }
    setLoading(false);
  };

  const saveMetric = async () => {
    if (!profile) return;

    setSaving(true);
    const { error } = await supabase.from('body_metrics').insert({
      user_id: profile.id,
      ...formData,
      recorded_at: new Date().toISOString(),
    });

    if (error) {
      Alert.alert('Oops', "Couldn't save your metrics. Check your connection and try again.");
    } else {
      setShowForm(false);
      setFormData({});
      loadMetrics();
    }
    setSaving(false);
  };

  const getTrend = (key: string): 'up' | 'down' | 'stable' => {
    if (metrics.length < 2) return 'stable';

    const recent = metrics[0] as any;
    const previous = metrics[1] as any;

    const recentValue = recent[key];
    const previousValue = previous[key];

    if (!recentValue || !previousValue) return 'stable';

    if (recentValue > previousValue) return 'up';
    if (recentValue < previousValue) return 'down';
    return 'stable';
  };

  const getChange = (key: string): string => {
    if (metrics.length < 2) return '';

    const recent = metrics[0] as any;
    const previous = metrics[1] as any;

    const recentValue = recent[key];
    const previousValue = previous[key];

    if (!recentValue || !previousValue) return '';

    const change = Math.abs(recentValue - previousValue);
    return change.toFixed(1);
  };

  const latestMetric = metrics[0];

  const renderStatCard = (measurement: { key: string; label: string; unit: string }) => {
    const value = latestMetric ? (latestMetric as any)[measurement.key] : null;
    const trend = getTrend(measurement.key);
    const change = getChange(measurement.key);

    return (
      <View key={measurement.key} style={[styles.statCard, { backgroundColor: theme.colors.surface }]}>
        <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]}>
          {measurement.label}
        </Text>
        <View style={styles.statRow}>
          <Text style={[styles.statValue, { color: theme.colors.text }]}>
            {value ? `${value}${measurement.unit}` : '--'}
          </Text>
          {trend !== 'stable' && change && (
            <View style={styles.trendContainer}>
              {trend === 'up' ? (
                <TrendingUp size={16} color={theme.colors.success} />
              ) : (
                <TrendingDown size={16} color={theme.colors.error} />
              )}
              <Text
                style={[
                  styles.trendText,
                  { color: trend === 'up' ? theme.colors.success : theme.colors.error },
                ]}>
                {change}{measurement.unit}
              </Text>
            </View>
          )}
        </View>
      </View>
    );
  };

  const renderHistory = () => {
    if (metrics.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Calendar size={48} color={theme.colors.textTertiary} />
          <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
            No measurements yet
          </Text>
          <Text style={[styles.emptySubtext, { color: theme.colors.textTertiary }]}>
            Start tracking your body measurements
          </Text>
        </View>
      );
    }

    return metrics.slice(0, 5).map(metric => (
      <View key={metric.id} style={[styles.historyItem, { backgroundColor: theme.colors.surfaceVariant }]}>
        <View style={styles.historyHeader}>
          <Calendar size={16} color={theme.colors.textSecondary} />
          <Text style={[styles.historyDate, { color: theme.colors.text }]}>
            {metric.recorded_at ? new Date(metric.recorded_at).toLocaleDateString() : '—'}
          </Text>
        </View>
        <View style={styles.historyMeasurements}>
          {MEASUREMENTS.map(m => {
            const value = (metric as any)[m.key];
            if (!value) return null;
            return (
              <Text key={m.key} style={[styles.historyMeasurement, { color: theme.colors.textSecondary }]}>
                {m.label}: {value}{m.unit}
              </Text>
            );
          })}
        </View>
      </View>
    ));
  };

  if (showForm) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <LinearGradient
          colors={[theme.colors.background, theme.colors.surfaceVariant]}
          style={styles.header}>
          <View style={styles.headerTop}>
            <TouchableOpacity onPress={() => setShowForm(false)} style={styles.backButton}>
              <ChevronLeft size={28} color={theme.colors.text} />
            </TouchableOpacity>
            <Text style={[styles.headerTitle, { color: theme.colors.accent }]}>
              NEW MEASUREMENT
            </Text>
            <View style={{ width: 28 }} />
          </View>
        </LinearGradient>

        <ScrollView contentContainerStyle={styles.formContent}>
          {MEASUREMENTS.map(measurement => (
            <View key={measurement.key} style={styles.inputGroup}>
              <Text style={[styles.inputLabel, { color: theme.colors.text }]}>
                {measurement.label} ({measurement.unit})
              </Text>
              <TextInput
                style={[
                  styles.input,
                  { backgroundColor: theme.colors.surface, color: theme.colors.text },
                ]}
                placeholder={`Enter ${measurement.label.toLowerCase()}`}
                placeholderTextColor={theme.colors.textTertiary}
                keyboardType="decimal-pad"
                value={
                  formData[measurement.key as keyof BodyMetric]
                    ? String(formData[measurement.key as keyof BodyMetric])
                    : ''
                }
                onChangeText={text => {
                  const value = parseFloat(text);
                  setFormData({
                    ...formData,
                    [measurement.key]: isNaN(value) ? undefined : value,
                  });
                }}
              />
            </View>
          ))}

          <View style={styles.inputGroup}>
            <Text style={[styles.inputLabel, { color: theme.colors.text }]}>Notes (Optional)</Text>
            <TextInput
              style={[
                styles.input,
                styles.notesInput,
                { backgroundColor: theme.colors.surface, color: theme.colors.text },
              ]}
              placeholder="Add any notes..."
              placeholderTextColor={theme.colors.textTertiary}
              multiline
              numberOfLines={3}
              value={formData.notes || ''}
              onChangeText={text => setFormData({ ...formData, notes: text })}
            />
          </View>

          <TouchableOpacity
            style={[styles.saveButton, { backgroundColor: theme.colors.accent }]}
            onPress={saveMetric}
            disabled={saving}>
            {saving ? (
              <ActivityIndicator size="small" color={theme.colors.text} />
            ) : (
              <>
                <Save size={20} color={theme.colors.text} />
                <Text style={[styles.saveButtonText, { color: theme.colors.accentForeground }]}>
                  Save Measurement
                </Text>
              </>
            )}
          </TouchableOpacity>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <LinearGradient
        colors={[theme.colors.background, theme.colors.surfaceVariant]}
        style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={styles.headerTop}>
          <View style={{ width: 28 }} />
          <Text style={[styles.headerTitle, { color: theme.colors.accent }]}>
            BODY METRICS
          </Text>
          <TouchableOpacity onPress={() => setShowForm(true)} style={styles.addButton}>
            <Plus size={28} color={theme.colors.accent} />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {loading ? (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.content}>
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            Current Measurements
          </Text>
          <View style={styles.statsGrid}>
            {MEASUREMENTS.map(renderStatCard)}
          </View>

          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Recent History</Text>
          {renderHistory()}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    paddingTop: 60,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
  },
  addButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '900',
    letterSpacing: 1,
  },
  content: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginTop: 16,
    marginBottom: 12,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 8,
  },
  statCard: {
    width: '48%',
    padding: 12,
    borderRadius: 2,
  },
  statLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 6,
  },
  statRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statValue: {
    fontSize: 20,
    fontWeight: '900',
  },
  trendContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  trendText: {
    fontSize: 12,
    fontWeight: '600',
  },
  historyItem: {
    padding: 16,
    borderRadius: 2,
    marginBottom: 12,
  },
  historyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  historyDate: {
    fontSize: 14,
    fontWeight: '700',
  },
  historyMeasurements: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  historyMeasurement: {
    fontSize: 12,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 60,
    gap: 12,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '700',
  },
  emptySubtext: {
    fontSize: 14,
  },
  formContent: {
    padding: 16,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderRadius: 2,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
    fontWeight: '600',
  },
  notesInput: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    borderRadius: 0,
    marginTop: 12,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
});
